import { useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import { useFilters } from "@/context/FilterContext";

function Panel({ n, title, children, className = "" }) {
  return (
    <div className={`bg-white p-6 rounded-xl border border-slate-200/60 kpi-shadow ${className}`}>
      <div className="flex items-center mb-4">
        <span className="text-[#93c5fd] mr-2 font-black text-lg">{n}.</span>
        <span className="text-[#1e40af] font-bold text-sm uppercase tracking-tight">{title}</span>
      </div>
      {children}
    </div>
  );
}

const SS_COLORS = {
  "Sistem Sinyal": "#1e40af",
  "Kereta": "#3b82f6",
  "Power": "#f59e0b",
  "PSD": "#22c55e",
  "Eksternal": "#94a3b8",
  "Human Error": "#ef4444",
};

export default function SubsystemPage() {
  const { params } = useFilters();
  const [failures, setFailures] = useState([]);

  useEffect(() => {
    (async () => {
      const f = await api.get("/failures", { params: { ...params, limit: 5000 } });
      setFailures(f.data.items);
    })();
  }, [params.start, params.end, params.line, params.subsystem]);

  const subsystems = useMemo(() => {
    const map = new Map();
    failures.forEach(f => {
      const key = f.equipment || "Unknown";
      const g = map.get(key) || { name: key, count: 0, downtime: 0, causes: new Map(), pics: new Map(), open: 0, closed: 0 };
      g.count++;
      g.downtime += (f.delay_seconds || 0) / 60;
      if (f.kategori_penyebab) g.causes.set(f.kategori_penyebab, (g.causes.get(f.kategori_penyebab) || 0) + 1);
      if (f.klasifikasi_pic) g.pics.set(f.klasifikasi_pic, (g.pics.get(f.klasifikasi_pic) || 0) + 1);
      if (f.status === "Closed") g.closed++; else g.open++;
      map.set(key, g);
    });
    return Array.from(map.values()).sort((a, b) => b.count - a.count).map(g => ({
      ...g,
      topCause: Array.from(g.causes.entries()).sort((a, b) => b[1] - a[1])[0]?.[0] || "—",
      topPic: Array.from(g.pics.entries()).sort((a, b) => b[1] - a[1])[0]?.[0] || "—",
      avgMttr: g.count > 0 ? g.downtime / g.count : 0,
    }));
  }, [failures]);

  const totalCount = failures.length || 1;

  return (
    <div className="space-y-6 pb-8" data-testid="detail-subsystem">
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {subsystems.map((s) => {
          const color = SS_COLORS[s.name] || "#64748b";
          const pct = (s.count / totalCount * 100);
          return (
            <div key={s.name} className="bg-white p-5 rounded-xl border border-slate-200/60 kpi-shadow relative overflow-hidden">
              <div className="absolute top-0 left-0 right-0 h-1" style={{ background: color }} />
              <div className="flex items-center justify-between mb-3">
                <div className="text-sm font-bold text-slate-900">{s.name}</div>
                <div className="text-[10px] font-bold uppercase tracking-widest text-white px-2 py-0.5 rounded" style={{ background: color }}>
                  {pct.toFixed(1)}%
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <div className="text-[10px] uppercase font-semibold text-slate-500 tracking-wider">Events</div>
                  <div className="text-2xl font-black text-slate-900 mt-0.5 tabular-nums">{s.count}</div>
                </div>
                <div>
                  <div className="text-[10px] uppercase font-semibold text-slate-500 tracking-wider">Downtime</div>
                  <div className="text-2xl font-black text-slate-900 mt-0.5 tabular-nums">{Math.round(s.downtime)}<span className="text-xs ml-1 text-slate-400">min</span></div>
                </div>
              </div>
              <div className="mt-3 pt-3 border-t border-slate-100 space-y-1.5">
                <div className="flex justify-between text-xs">
                  <span className="text-slate-500">Avg MTTR</span>
                  <span className="font-semibold text-slate-900">{s.avgMttr.toFixed(1)} min</span>
                </div>
                <div className="flex justify-between text-xs">
                  <span className="text-slate-500">Top Cause</span>
                  <span className="font-semibold text-slate-900 truncate max-w-[140px]" title={s.topCause}>{s.topCause}</span>
                </div>
                <div className="flex justify-between text-xs">
                  <span className="text-slate-500">Top PIC</span>
                  <span className="font-semibold text-slate-900">{s.topPic}</span>
                </div>
                <div className="flex justify-between text-xs">
                  <span className="text-slate-500">Closed / Open</span>
                  <span className="font-semibold text-slate-900">{s.closed} <span className="text-slate-400">/</span> {s.open}</span>
                </div>
              </div>
            </div>
          );
        })}
      </div>

      <Panel n="1" title="Subsystem Comparison Table">
        <div className="overflow-x-auto trams-scroll">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-[10px] uppercase tracking-widest text-slate-500 border-b border-slate-200">
                <th className="text-left font-semibold pb-3">Subsystem</th>
                <th className="text-right font-semibold pb-3">Events</th>
                <th className="text-right font-semibold pb-3">%</th>
                <th className="text-right font-semibold pb-3">Downtime</th>
                <th className="text-right font-semibold pb-3">Avg MTTR</th>
                <th className="text-left font-semibold pb-3">Top Cause</th>
                <th className="text-left font-semibold pb-3">Top PIC</th>
                <th className="text-right font-semibold pb-3">Open</th>
              </tr>
            </thead>
            <tbody>
              {subsystems.map(s => (
                <tr key={s.name} className="border-b border-slate-100">
                  <td className="py-3 font-semibold text-slate-900 flex items-center gap-2">
                    <div className="w-2 h-2 rounded-full" style={{ background: SS_COLORS[s.name] || "#64748b" }} /> {s.name}
                  </td>
                  <td className="py-3 text-right tabular-nums">{s.count}</td>
                  <td className="py-3 text-right tabular-nums">{(s.count / totalCount * 100).toFixed(1)}%</td>
                  <td className="py-3 text-right tabular-nums">{Math.round(s.downtime)} min</td>
                  <td className="py-3 text-right tabular-nums">{s.avgMttr.toFixed(1)} min</td>
                  <td className="py-3 max-w-[180px] truncate" title={s.topCause}>{s.topCause}</td>
                  <td className="py-3">{s.topPic}</td>
                  <td className="py-3 text-right tabular-nums">{s.open}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Panel>
    </div>
  );
}
